import { useCallback, useEffect } from "react";
import { useDataLayer } from "./useDataLayer";
import { useLocation } from "react-router-dom";
import { useCustomerManagementStore, usePaymentStore } from "./store";
import {
  CREATE_ACCOUNT_ROUTE,
  GoogleAnalyticsEventsEnum,
  GoogleAnalyticsKYCStepsEnum,
} from "_constants";
import { useKYCStatus } from "./useKYCStatus";
import {
  getAccountPlatformFromGroupName,
  getAccountTypeNameAndDescription,
} from "_helpers";

export const useEventsTracking = () => {
  const { trackDataLayerUser, trackDataLayerAuthActions, pushDataLayerEvents } =
    useDataLayer();
  const { pathname } = useLocation();

  const {
    customerManagement: {
      signInStatus,
      socialSignInStatus,
      socialSignupStatus,
      signupStatus,
      submitTradingAccountsStatus,
      submittedTradingAccountData,
      documentsStatus,
      introducingBrokerApplicationStatus,
      submitReferralLinkStatus,
      submitEmploymentDetailsStatus,
      submitPersonalDetailsStatus,
      submitFinancialDetailsStatus,
    },
  } = useCustomerManagementStore();

  const { isFirstTime } = useKYCStatus();

  const {
    payment: { transfersStatus, initiateStkPushStatus, withdrawStatus },
  } = usePaymentStore();

  useEffect(() => {
    if (signInStatus === "success" || socialSignInStatus === "success") {
      trackDataLayerAuthActions(GoogleAnalyticsEventsEnum.Signin);
    }
  }, [signInStatus, socialSignInStatus, trackDataLayerAuthActions]);

  useEffect(() => {
    if (signupStatus === "success" || socialSignupStatus === "success") {
      trackDataLayerAuthActions(GoogleAnalyticsEventsEnum.Signup);
    }
  }, [signupStatus, socialSignupStatus, trackDataLayerAuthActions]);

  const handleAccountCreationSuccessByPlatform = useCallback(() => {
    const accountGroupName = submittedTradingAccountData?.accountType || "";

    const accountPlatform = getAccountPlatformFromGroupName(accountGroupName);
    const { accountTypeName } =
      getAccountTypeNameAndDescription(accountGroupName);

    const eventData = {
      status: accountGroupName?.includes("demo") ? "Demo" : "Live",
      page_location: CREATE_ACCOUNT_ROUTE,
      account_type: accountTypeName.toLowerCase(),
    };

    if (accountPlatform === "MT5") {
      eventData.event = GoogleAnalyticsEventsEnum.MT5CreateAccountSuccess;
    } else {
      if (accountTypeName === "CLASSIC") {
        eventData.event =
          GoogleAnalyticsEventsEnum.EFXLiteCreateAccountSuccess;
      } else {
        eventData.event = GoogleAnalyticsEventsEnum.EFXProCreateAccountSuccess;
      }
    }

    pushDataLayerEvents(eventData);
  }, [pushDataLayerEvents, submittedTradingAccountData?.accountType]);

  useEffect(() => {
    if (submitTradingAccountsStatus === "success") {
      pushDataLayerEvents({
        event: GoogleAnalyticsEventsEnum.CreateTradingAccount,
      });

      handleAccountCreationSuccessByPlatform();
    }
  }, [
    submitTradingAccountsStatus,
    pushDataLayerEvents,
    handleAccountCreationSuccessByPlatform,
  ]);

  useEffect(() => {
    if (documentsStatus === "success" && isFirstTime) {
      pushDataLayerEvents({
        event: GoogleAnalyticsKYCStepsEnum.IDSubmission,
      });
    }
  }, [documentsStatus, isFirstTime, pushDataLayerEvents]);

  useEffect(() => {
    if (transfersStatus === "success") {
      pushDataLayerEvents({ event: GoogleAnalyticsEventsEnum.Transfer });
    }
  }, [transfersStatus, pushDataLayerEvents]);

  useEffect(() => {
    if (initiateStkPushStatus === "success") {
      pushDataLayerEvents({ event: GoogleAnalyticsEventsEnum.Deposit });
    }
  }, [initiateStkPushStatus, pushDataLayerEvents]);

  useEffect(() => {
    if (withdrawStatus === "success") {
      pushDataLayerEvents({ event: GoogleAnalyticsEventsEnum.Withdraw });
    }
  }, [withdrawStatus, pushDataLayerEvents]);

  useEffect(() => {
    if (introducingBrokerApplicationStatus === "success") {
      pushDataLayerEvents({
        event: GoogleAnalyticsEventsEnum.SubmitIntroducingBrokerApplication,
      });
    }
  }, [introducingBrokerApplicationStatus, pushDataLayerEvents]);

  useEffect(() => {
    if (submitReferralLinkStatus === "success") {
      pushDataLayerEvents({
        event: GoogleAnalyticsEventsEnum.CreateReferralLink,
      });
    }
  }, [submitReferralLinkStatus, pushDataLayerEvents]);

  useEffect(() => {
    if (submitPersonalDetailsStatus === "success") {
      pushDataLayerEvents({
        event: GoogleAnalyticsKYCStepsEnum.Started,
      });
      pushDataLayerEvents({
        event: GoogleAnalyticsKYCStepsEnum.TaxIdentification,
      });
    }
  }, [pushDataLayerEvents, submitPersonalDetailsStatus]);

  useEffect(() => {
    if (submitEmploymentDetailsStatus === "success") {
      pushDataLayerEvents({
        event: GoogleAnalyticsKYCStepsEnum.EmploymentIdentification,
      });
    }
  }, [pushDataLayerEvents, submitEmploymentDetailsStatus]);

  useEffect(() => {
    if (submitFinancialDetailsStatus === "success") {
      pushDataLayerEvents({
        event: GoogleAnalyticsKYCStepsEnum.IncomeIdentification,
      });
    }
  }, [pushDataLayerEvents, submitFinancialDetailsStatus]);

  useEffect(() => {
    trackDataLayerUser();
  }, [trackDataLayerUser, pathname]);

  return;
};
